import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { searchProject } from '../../../../../helpers/project-helper';

const statusClassMap = {
    ACTIVE: 'bg-soft-success text-success',
    SUSPENDED: 'bg-soft-warning text-warning',
    INACTIVE: 'bg-soft-danger text-danger',
};

export const CustomerProjectsTabContent = ({ customerId, formatDate }) => {
    const navigate = useNavigate();
    const { token } = useSelector((state) => state.auth);

    const [isLoading, setIsLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const [projects, setProjects] = React.useState([]);
    const [total, setTotal] = React.useState(0);

    React.useEffect(() => {
        const fetchProjects = async () => {
            if (!token || !customerId) {
                setError('No se pudieron obtener los proyectos del cliente.');
                setProjects([]);
                setTotal(0);
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            setError('');

            const resp = await searchProject(token, {
                field: 'customer',
                value: customerId,
                includeDeleted: false,
                limit: 100,
                offset: 0,
            });

            if (!resp?.success) {
                setError(resp?.message || 'No se pudieron obtener los proyectos del cliente.');
                setProjects([]);
                setTotal(0);
                setIsLoading(false);
                return;
            }

            const fetchedProjects = Array.isArray(resp.data) ? resp.data : [];
            setProjects(fetchedProjects);
            setTotal(Number(resp.total) || fetchedProjects.length);
            setIsLoading(false);
        };

        fetchProjects();
    }, [token, customerId]);

    const statusSummary = React.useMemo(() => {
        return projects.reduce((accumulator, project) => {
            const status = project?.status || 'INACTIVE';
            const nextValue = (accumulator[status] || 0) + 1;
            return {
                ...accumulator,
                [status]: nextValue,
            };
        }, {
            ACTIVE: 0,
            SUSPENDED: 0,
            INACTIVE: 0,
        });
    }, [projects]);

    const handleProjectClick = (projectId, projectName) => {
        if (!projectId) return;

        navigate(`/project/detail/${projectId}`, {
            state: {
                projectName,
            },
        });
    };

    return (
        <div className="about-section mb-2">
            <div className="mb-4 d-flex align-items-center justify-content-between">
                <h5 className="fw-bold mb-0">Proyectos del cliente</h5>
                <div className="d-flex align-items-center gap-2">
                    <span className="badge bg-soft-primary text-primary">{total} total</span>
                    <button type="button" className="btn btn-primary btn-sm" onClick={() => navigate('/project/create')}>
                        Nuevo proyecto
                    </button>
                </div>
            </div>

            {isLoading && (
                <div className="py-3 text-muted">Cargando proyectos...</div>
            )}

            {!isLoading && error && (
                <div className="alert alert-danger py-2 mb-0" role="alert">
                    {error}
                </div>
            )}

            {!isLoading && !error && (
                <>
                    <div className="row g-3 mb-4">
                        <div className="col-md-4">
                            <div className="border border-dashed border-gray-5 rounded-2 p-3">
                                <div className="text-muted fs-12">Activos</div>
                                <h5 className="mb-0 mt-1">{statusSummary.ACTIVE}</h5>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="border border-dashed border-gray-5 rounded-2 p-3">
                                <div className="text-muted fs-12">Suspendidos</div>
                                <h5 className="mb-0 mt-1">{statusSummary.SUSPENDED}</h5>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="border border-dashed border-gray-5 rounded-2 p-3">
                                <div className="text-muted fs-12">Inactivos</div>
                                <h5 className="mb-0 mt-1">{statusSummary.INACTIVE}</h5>
                            </div>
                        </div>
                    </div>

                    <div className="table-responsive">
                        <table className="table table-hover mb-0">
                            <thead>
                                <tr>
                                    <th className="text-uppercase fs-11 fw-semibold py-3">Código</th>
                                    <th className="text-uppercase fs-11 fw-semibold py-3">Nombre</th>
                                    <th className="text-uppercase fs-11 fw-semibold py-3">Estatus</th>
                                    <th className="text-uppercase fs-11 fw-semibold py-3">Inicio</th>
                                    <th className="text-uppercase fs-11 fw-semibold py-3">Fin</th>
                                </tr>
                            </thead>
                            <tbody>
                                {projects.map((project) => (
                                    <tr key={project._id}>
                                        <td className="py-2">
                                            {project._id ? (
                                                <span className="link__nocolor badge bg-soft-primary text-primary" onClick={() => handleProjectClick(project._id, project.name)}>
                                                    {project.code || '-'}
                                                </span>
                                            ) : (project.code || '-')}
                                        </td>
                                        <td className="py-2 fw-semibold">
                                            {project._id ? (
                                                <span className="link" onClick={() => handleProjectClick(project._id, project.name)}>{project.name || '-'}</span>
                                            ) : (project.name || '-')}
                                        </td>
                                        <td className="py-2">
                                            <span className={`badge ${statusClassMap[project.status] || 'bg-soft-secondary text-secondary'}`}>
                                                {project.status || '-'}
                                            </span>
                                        </td>
                                        <td className="py-2">{formatDate(project.startDate)}</td>
                                        <td className="py-2">{formatDate(project.endDate)}</td>
                                    </tr>
                                ))}

                                {projects.length === 0 && (
                                    <tr>
                                        <td colSpan={5} className="text-center text-muted py-4">
                                            No hay proyectos para este cliente.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
};
